import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { useTranslation } from 'react-i18next';

import type {
  CompanyModuleDto,
  CompanyOutboundPolicyEventDto,
  CompanyOutboundPolicyEventsFiltersDto,
  SafeAutomationConfigDto,
} from '@/shared/api/companies';

import { useCompanyOutboundPolicyEvents } from '../model/useCompanyModules';

const DEFAULT_SAFE_AUTOMATION_CONFIG: SafeAutomationConfigDto = {
  enabled_sources: ['amocrm_digital_pipeline'],
  min_delay_seconds: 30,
  random_delay_seconds: 0,
  daily_limit_per_channel: 100,
  quiet_hours: {
    enabled: false,
    start: '21:00',
    end: '09:00',
    timezone: 'UTC',
  },
  dry_run: false,
};

const SAFE_AUTOMATION_SOURCES: SafeAutomationConfigDto['enabled_sources'] = [
  'amocrm_digital_pipeline',
  'ai_assistant',
  'campaign',
];

const AUDIT_FILTER_ALL = 'all';
const AUDIT_LIMITS = [20, 50, 100] as const;
const AUDIT_DECISIONS: CompanyOutboundPolicyEventDto['decision'][] = [
  'allow',
  'deny',
  'dry_run',
  'queue',
];
const AUDIT_REASONS: CompanyOutboundPolicyEventDto['reason'][] = [
  'policy_allowed',
  'quiet_hours',
  'delay_required',
  'daily_limit_reached',
  'dry_run',
  'source_not_enabled',
  'channel_not_scoped',
  'module_disabled',
];

type AuditSourceFilter =
  | CompanyOutboundPolicyEventDto['source']
  | typeof AUDIT_FILTER_ALL;
type AuditDecisionFilter =
  | CompanyOutboundPolicyEventDto['decision']
  | typeof AUDIT_FILTER_ALL;
type AuditReasonFilter =
  | CompanyOutboundPolicyEventDto['reason']
  | typeof AUDIT_FILTER_ALL;
type SafeAutomationTab = 'policy' | 'audit';

function parseEnabledSources(
  raw: unknown
): SafeAutomationConfigDto['enabled_sources'] {
  if (!Array.isArray(raw))
    return DEFAULT_SAFE_AUTOMATION_CONFIG.enabled_sources;

  return raw.filter(
    (source): source is SafeAutomationConfigDto['enabled_sources'][number] =>
      SAFE_AUTOMATION_SOURCES.includes(
        source as SafeAutomationConfigDto['enabled_sources'][number]
      )
  );
}

function parseNumberConfig(
  raw: unknown,
  fallback: number,
  min: number,
  max: number
) {
  const parsed = Number(raw ?? fallback);

  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) return fallback;
  if (parsed < min || parsed > max) return fallback;

  return parsed;
}

function parseQuietHours(raw: unknown): SafeAutomationConfigDto['quiet_hours'] {
  const fallback = DEFAULT_SAFE_AUTOMATION_CONFIG.quiet_hours;

  if (!raw || typeof raw !== 'object') return fallback;

  const quietHours = raw as Partial<SafeAutomationConfigDto['quiet_hours']>;

  return {
    enabled:
      typeof quietHours.enabled === 'boolean'
        ? quietHours.enabled
        : fallback.enabled,
    start:
      typeof quietHours.start === 'string' && quietHours.start.trim() !== ''
        ? quietHours.start
        : fallback.start,
    end:
      typeof quietHours.end === 'string' && quietHours.end.trim() !== ''
        ? quietHours.end
        : fallback.end,
    timezone:
      typeof quietHours.timezone === 'string' &&
      quietHours.timezone.trim() !== ''
        ? quietHours.timezone
        : fallback.timezone,
  };
}

function parseConfig(config: Record<string, unknown>): SafeAutomationConfigDto {
  return {
    enabled_sources: parseEnabledSources(config.enabled_sources),
    min_delay_seconds: parseNumberConfig(
      config.min_delay_seconds,
      DEFAULT_SAFE_AUTOMATION_CONFIG.min_delay_seconds,
      0,
      86400
    ),
    random_delay_seconds: parseNumberConfig(
      config.random_delay_seconds,
      DEFAULT_SAFE_AUTOMATION_CONFIG.random_delay_seconds,
      0,
      86400
    ),
    daily_limit_per_channel: parseNumberConfig(
      config.daily_limit_per_channel,
      DEFAULT_SAFE_AUTOMATION_CONFIG.daily_limit_per_channel,
      1,
      10000
    ),
    quiet_hours: parseQuietHours(config.quiet_hours),
    dry_run: Boolean(config.dry_run ?? DEFAULT_SAFE_AUTOMATION_CONFIG.dry_run),
  };
}

function parseIntegerDraft(value: string, min: number, max: number) {
  if (value.trim() === '') return null;

  const parsed = Number(value);

  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) return null;
  if (parsed < min || parsed > max) return null;

  return parsed;
}

function formatDateTime(value: string | null) {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString();
}

function decisionColor(
  decision: CompanyOutboundPolicyEventDto['decision']
): 'success' | 'warning' | 'default' {
  if (decision === 'allow') return 'success';
  if (decision === 'dry_run' || decision === 'queue') return 'warning';

  return 'default';
}

function shortHash(value: string) {
  if (value.length <= 18) return value;

  return `${value.slice(0, 10)}...${value.slice(-6)}`;
}

function getErrorMessage(error: unknown) {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;

    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
  }

  return null;
}

export function SafeAutomationConfigPanel({
  companyId,
  module,
  accentColor,
  disabled,
  onSave,
}: {
  companyId: string;
  module: CompanyModuleDto;
  accentColor: string;
  disabled: boolean;
  onSave: (config: SafeAutomationConfigDto) => void;
}) {
  const { t } = useTranslation('company');
  const initial = useMemo(
    () => parseConfig(module.config),
    [
      module.config.enabled_sources,
      module.config.min_delay_seconds,
      module.config.random_delay_seconds,
      module.config.daily_limit_per_channel,
      module.config.quiet_hours,
      module.config.dry_run,
    ]
  );
  const [minDelay, setMinDelay] = useState(String(initial.min_delay_seconds));
  const [dailyLimit, setDailyLimit] = useState(
    String(initial.daily_limit_per_channel)
  );
  const [dryRun, setDryRun] = useState(initial.dry_run);
  const [enabledSources, setEnabledSources] = useState(initial.enabled_sources);
  const [activeTab, setActiveTab] = useState<SafeAutomationTab>('policy');
  const [auditSource, setAuditSource] =
    useState<AuditSourceFilter>(AUDIT_FILTER_ALL);
  const [auditDecision, setAuditDecision] =
    useState<AuditDecisionFilter>(AUDIT_FILTER_ALL);
  const [auditReason, setAuditReason] =
    useState<AuditReasonFilter>(AUDIT_FILTER_ALL);
  const [auditChannelId, setAuditChannelId] = useState('');
  const [auditLimit, setAuditLimit] =
    useState<CompanyOutboundPolicyEventsFiltersDto['limit']>(50);
  const minDelayValue = parseIntegerDraft(minDelay, 0, 86400);
  const dailyLimitValue = parseIntegerDraft(dailyLimit, 1, 10000);
  const canSave =
    !disabled && minDelayValue !== null && dailyLimitValue !== null;
  const auditFilters = useMemo<CompanyOutboundPolicyEventsFiltersDto>(() => {
    const channelId = auditChannelId.trim();

    return {
      limit: auditLimit,
      channel_id: channelId || undefined,
      source: auditSource === AUDIT_FILTER_ALL ? undefined : auditSource,
      decision: auditDecision === AUDIT_FILTER_ALL ? undefined : auditDecision,
      reason: auditReason === AUDIT_FILTER_ALL ? undefined : auditReason,
    };
  }, [auditChannelId, auditDecision, auditLimit, auditReason, auditSource]);
  const auditQuery = useCompanyOutboundPolicyEvents(companyId, auditFilters);
  const auditEvents = auditQuery.data?.events ?? [];
  const auditErrorMessage = getErrorMessage(auditQuery.error);
  const auditStats = useMemo(
    () =>
      auditEvents.reduce(
        (acc, event) => ({
          total: acc.total + 1,
          allow: acc.allow + (event.decision === 'allow' ? 1 : 0),
          deny: acc.deny + (event.decision === 'deny' ? 1 : 0),
          dry_run: acc.dry_run + (event.decision === 'dry_run' ? 1 : 0),
          queue: acc.queue + (event.decision === 'queue' ? 1 : 0),
        }),
        { total: 0, allow: 0, deny: 0, dry_run: 0, queue: 0 }
      ),
    [auditEvents]
  );
  const hasAuditFilters =
    auditSource !== AUDIT_FILTER_ALL ||
    auditDecision !== AUDIT_FILTER_ALL ||
    auditReason !== AUDIT_FILTER_ALL ||
    auditChannelId.trim() !== '';

  useEffect(() => {
    setMinDelay(String(initial.min_delay_seconds));
    setDailyLimit(String(initial.daily_limit_per_channel));
    setDryRun(initial.dry_run);
    setEnabledSources(initial.enabled_sources);
  }, [
    initial.enabled_sources,
    initial.min_delay_seconds,
    initial.daily_limit_per_channel,
    initial.dry_run,
  ]);

  const toggleSource = (
    source: SafeAutomationConfigDto['enabled_sources'][number]
  ) => {
    setEnabledSources((prev) =>
      prev.includes(source)
        ? prev.filter((item) => item !== source)
        : [...prev, source]
    );
  };

  const resetAuditFilters = () => {
    setAuditSource(AUDIT_FILTER_ALL);
    setAuditDecision(AUDIT_FILTER_ALL);
    setAuditReason(AUDIT_FILTER_ALL);
    setAuditChannelId('');
  };

  return (
    <Stack
      spacing={1.5}
      sx={{
        mt: 0.5,
        p: 1.5,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
        borderRadius: 1,
        bgcolor: alpha(accentColor, 0.025),
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, value: SafeAutomationTab) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 36,
            px: 1.25,
            fontSize: 12,
            fontWeight: 800,
            textTransform: 'none',
          },
          '& .MuiTab-root.Mui-selected': {
            color: accentColor,
          },
          '& .MuiTabs-indicator': {
            bgcolor: accentColor,
          },
        }}
      >
        <Tab value="policy" label={t('modules.safeAutomation.policyTitle')} />
        <Tab value="audit" label={t('modules.safeAutomation.audit.title')} />
      </Tabs>

      {activeTab === 'policy' ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: 'text.primary',
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: 0,
                }}
              >
                {t('modules.safeAutomation.policyTitle')}
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: 12,
                  lineHeight: 1.35,
                }}
              >
                {t('modules.safeAutomation.policyCaption')}
              </Typography>
            </Stack>

            <Box
              sx={{
                px: 1,
                py: 0.5,
                border: '1px solid',
                borderColor: alpha(accentColor, 0.26),
                borderRadius: 1,
                color: accentColor,
                bgcolor: alpha(accentColor, 0.08),
                fontSize: 11,
                fontWeight: 800,
                lineHeight: 1.2,
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              amoCRM
            </Box>
          </Box>

          <FormGroup
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 0.5,
            }}
          >
            {SAFE_AUTOMATION_SOURCES.map((source) => (
              <FormControlLabel
                key={source}
                sx={{
                  m: 0,
                  alignSelf: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    fontSize: 14,
                    lineHeight: 1.35,
                  },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={enabledSources.includes(source)}
                    disabled={disabled}
                    onChange={() => toggleSource(source)}
                  />
                }
                label={t(`modules.safeAutomation.sources.${source}`)}
              />
            ))}
          </FormGroup>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <TextField
              size="small"
              type="number"
              label={t('modules.safeAutomation.delay')}
              value={minDelay}
              disabled={disabled}
              error={minDelay !== '' && minDelayValue === null}
              inputProps={{ min: 0, max: 86400, step: 1 }}
              onChange={(event) => setMinDelay(event.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 1,
                },
              }}
            />
            <TextField
              size="small"
              type="number"
              label={t('modules.safeAutomation.dailyLimit')}
              value={dailyLimit}
              disabled={disabled}
              error={dailyLimit !== '' && dailyLimitValue === null}
              inputProps={{ min: 1, max: 10000, step: 1 }}
              onChange={(event) => setDailyLimit(event.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
              gap: 1.25,
              alignItems: 'center',
            }}
          >
            <FormControlLabel
              sx={{
                m: 0,
                minWidth: 0,
                alignSelf: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  fontSize: 14,
                  lineHeight: 1.35,
                },
              }}
              control={
                <Switch
                  size="small"
                  checked={dryRun}
                  disabled={disabled}
                  onChange={(event) => setDryRun(event.target.checked)}
                />
              }
              label={t('modules.safeAutomation.dryRun')}
            />

            <Button
              variant="contained"
              disabled={!canSave}
              onClick={() => {
                if (!canSave) return;

                onSave({
                  enabled_sources: enabledSources,
                  min_delay_seconds: minDelayValue,
                  random_delay_seconds: initial.random_delay_seconds,
                  daily_limit_per_channel: dailyLimitValue,
                  quiet_hours: initial.quiet_hours,
                  dry_run: dryRun,
                });
              }}
              sx={{
                minHeight: 38,
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {t('modules.safeAutomation.save')}
            </Button>
          </Box>
        </>
      ) : null}

      {activeTab === 'audit' ? (
        <Stack spacing={1.25}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: 'text.primary',
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: 0,
                }}
              >
                {t('modules.safeAutomation.audit.title')}
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: 12,
                  lineHeight: 1.35,
                }}
              >
                {t('modules.safeAutomation.audit.caption')}
              </Typography>
            </Stack>

            <Tooltip title={t('modules.safeAutomation.audit.refresh')} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={auditQuery.isFetching}
                  onClick={() => auditQuery.refetch()}
                  sx={{
                    width: 34,
                    height: 34,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <RefreshOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: '1fr 1fr 1fr minmax(150px, 1fr) 104px',
              },
              gap: 1,
            }}
          >
            <TextField
              select
              size="small"
              label={t('modules.safeAutomation.audit.filters.source')}
              value={auditSource}
              onChange={(event) =>
                setAuditSource(event.target.value as AuditSourceFilter)
              }
            >
              <MenuItem value={AUDIT_FILTER_ALL}>
                {t('modules.safeAutomation.audit.filters.all')}
              </MenuItem>
              {SAFE_AUTOMATION_SOURCES.map((source) => (
                <MenuItem key={source} value={source}>
                  {t(`modules.safeAutomation.sources.${source}`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t('modules.safeAutomation.audit.filters.decision')}
              value={auditDecision}
              onChange={(event) =>
                setAuditDecision(event.target.value as AuditDecisionFilter)
              }
            >
              <MenuItem value={AUDIT_FILTER_ALL}>
                {t('modules.safeAutomation.audit.filters.all')}
              </MenuItem>
              {AUDIT_DECISIONS.map((decision) => (
                <MenuItem key={decision} value={decision}>
                  {t(`modules.safeAutomation.audit.decisions.${decision}`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t('modules.safeAutomation.audit.filters.reason')}
              value={auditReason}
              onChange={(event) =>
                setAuditReason(event.target.value as AuditReasonFilter)
              }
            >
              <MenuItem value={AUDIT_FILTER_ALL}>
                {t('modules.safeAutomation.audit.filters.all')}
              </MenuItem>
              {AUDIT_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {t(`modules.safeAutomation.audit.reasons.${reason}`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label={t('modules.safeAutomation.audit.filters.channel')}
              value={auditChannelId}
              onChange={(event) => setAuditChannelId(event.target.value)}
            />

            <TextField
              select
              size="small"
              label={t('modules.safeAutomation.audit.filters.limit')}
              value={String(auditLimit ?? 50)}
              onChange={(event) => setAuditLimit(Number(event.target.value))}
            >
              {AUDIT_LIMITS.map((limit) => (
                <MenuItem key={limit} value={String(limit)}>
                  {limit}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={t('modules.safeAutomation.audit.summary.total', {
                count: auditStats.total,
              })}
              sx={{ borderRadius: 1 }}
            />
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={t('modules.safeAutomation.audit.summary.allow', {
                count: auditStats.allow,
              })}
              sx={{ borderRadius: 1 }}
            />
            <Chip
              size="small"
              variant="outlined"
              label={t('modules.safeAutomation.audit.summary.deny', {
                count: auditStats.deny,
              })}
              sx={{ borderRadius: 1 }}
            />
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('modules.safeAutomation.audit.summary.dry_run', {
                count: auditStats.dry_run,
              })}
              sx={{ borderRadius: 1 }}
            />
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('modules.safeAutomation.audit.summary.queue', {
                count: auditStats.queue,
              })}
              sx={{ borderRadius: 1 }}
            />
            {hasAuditFilters ? (
              <Button
                size="small"
                variant="text"
                onClick={resetAuditFilters}
                sx={{
                  minHeight: 24,
                  borderRadius: 1,
                  fontSize: 12,
                  fontWeight: 800,
                  px: 0.75,
                }}
              >
                {t('modules.safeAutomation.audit.filters.reset')}
              </Button>
            ) : null}
          </Stack>

          {auditQuery.isPending ? (
            <Typography variant="body2" color="text.secondary">
              {t('modules.safeAutomation.audit.loading')}
            </Typography>
          ) : null}
          {auditQuery.isError ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={auditQuery.isFetching}
                  onClick={() => auditQuery.refetch()}
                  sx={{ borderRadius: 1, fontWeight: 800 }}
                >
                  {t('modules.safeAutomation.audit.retry')}
                </Button>
              }
              sx={{ borderRadius: 1 }}
            >
              <AlertTitle sx={{ fontSize: 13, fontWeight: 800 }}>
                {t('modules.safeAutomation.audit.error')}
              </AlertTitle>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.4 }}>
                {auditErrorMessage
                  ? t('modules.safeAutomation.audit.errorWithMessage', {
                      message: auditErrorMessage,
                    })
                  : t('modules.safeAutomation.audit.errorCaption')}
              </Typography>
            </Alert>
          ) : null}
          {!auditQuery.isPending &&
          !auditQuery.isError &&
          auditEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {hasAuditFilters
                ? t('modules.safeAutomation.audit.emptyFiltered')
                : t('modules.safeAutomation.audit.empty')}
            </Typography>
          ) : null}
          {!auditQuery.isPending &&
          !auditQuery.isError &&
          auditEvents.length > 0 ? (
            <TableContainer
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflowX: 'auto',
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 860,
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    borderColor: 'divider',
                    verticalAlign: 'top',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 170 }}>
                      {t('modules.safeAutomation.audit.columns.time')}
                    </TableCell>
                    <TableCell sx={{ width: 220 }}>
                      {t('modules.safeAutomation.audit.columns.event')}
                    </TableCell>
                    <TableCell sx={{ width: 250 }}>
                      {t('modules.safeAutomation.audit.columns.policy')}
                    </TableCell>
                    <TableCell sx={{ width: 220 }}>
                      {t('modules.safeAutomation.audit.columns.scope')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditEvents.map((event) => {
                    const snapshot = (event.policy_snapshot ??
                      {}) as Partial<SafeAutomationConfigDto>;

                    return (
                      <TableRow key={event.id} hover>
                        <TableCell>
                          <Stack spacing={0.35}>
                            <Typography
                              sx={{ fontSize: 12.5, fontWeight: 700 }}
                            >
                              {formatDateTime(event.created_at)}
                            </Typography>
                            {event.not_before ? (
                              <Typography
                                sx={{ color: 'text.secondary', fontSize: 12 }}
                              >
                                {t('modules.safeAutomation.audit.notBefore', {
                                  value: formatDateTime(event.not_before),
                                })}
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.6}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t(
                                  `modules.safeAutomation.sources.${event.source}`
                                )}
                                sx={{ borderRadius: 1 }}
                              />
                              <Chip
                                size="small"
                                color={decisionColor(event.decision)}
                                label={t(
                                  `modules.safeAutomation.audit.decisions.${event.decision}`
                                )}
                                sx={{ borderRadius: 1 }}
                              />
                            </Stack>
                            <Typography
                              sx={{ color: 'text.secondary', fontSize: 12 }}
                            >
                              {t('modules.safeAutomation.audit.hash', {
                                value: shortHash(event.message_hash),
                              })}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t(
                                `modules.safeAutomation.audit.reasons.${event.reason}`
                              )}
                              sx={{
                                alignSelf: 'flex-start',
                                borderRadius: 1,
                                maxWidth: '100%',
                              }}
                            />
                            <Typography
                              sx={{
                                color: 'text.secondary',
                                fontSize: 12,
                                lineHeight: 1.35,
                              }}
                            >
                              {t(
                                'modules.safeAutomation.audit.snapshot.delay',
                                {
                                  value: snapshot.min_delay_seconds ?? '-',
                                }
                              )}
                              {' / '}
                              {t(
                                'modules.safeAutomation.audit.snapshot.randomDelay',
                                {
                                  value: snapshot.random_delay_seconds ?? '-',
                                }
                              )}
                              {' / '}
                              {t(
                                'modules.safeAutomation.audit.snapshot.dailyLimit',
                                {
                                  value:
                                    snapshot.daily_limit_per_channel ?? '-',
                                }
                              )}
                              {' / '}
                              {snapshot.dry_run
                                ? t(
                                    'modules.safeAutomation.audit.snapshot.dryRunEnabled'
                                  )
                                : t(
                                    'modules.safeAutomation.audit.snapshot.dryRunDisabled'
                                  )}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.4}>
                            <Typography
                              title={event.channel_id}
                              sx={{
                                fontSize: 12,
                                fontFamily: 'monospace',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {event.channel_id}
                            </Typography>
                            <Typography
                              title={event.chat_id}
                              sx={{
                                color: 'text.secondary',
                                fontSize: 12,
                                fontFamily: 'monospace',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {event.chat_id}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}
